import { AbsoluteFill, interpolate, useCurrentFrame } from "remotion";
import {
  Caption,
  CodePanel,
  COLORS,
  FONTS,
  Panel,
  PipelineGraph,
  SceneTransition,
  SlideTimeBar,
  type PipeNode,
} from "../../coldcompute";
import { SCENE } from "./constants";
import {
  HOOK,
  FIX1,
  FIX2,
  FIX3,
  FIX4,
  RECAP,
  LESSON,
  OUTRO,
} from "./script";

const center: React.CSSProperties = {
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
};

const Kicker: React.FC<{ text: string; color: string }> = ({ text, color }) => (
  <div
    style={{
      fontFamily: FONTS.mono,
      fontSize: 22,
      letterSpacing: "0.22em",
      textTransform: "uppercase",
      color,
      marginBottom: 22,
    }}
  >
    ▍{text}
  </div>
);

const Big: React.FC<{ children: React.ReactNode; size?: number }> = ({
  children,
  size = 92,
}) => (
  <div
    style={{
      fontFamily: FONTS.display,
      fontWeight: 700,
      fontSize: size,
      lineHeight: 0.98,
      letterSpacing: "-0.02em",
      color: COLORS.text,
      textAlign: "center",
      maxWidth: 1560,
    }}
  >
    {children}
  </div>
);

/* Shared FIX layout: a number chip + headline, then a before→after pair of panels. */
const FixHeader: React.FC<{ n: number; title: string; color: string }> = ({
  n,
  title,
  color,
}) => (
  <div style={{ display: "flex", alignItems: "center", gap: 20, marginBottom: 26 }}>
    <div
      style={{
        fontFamily: FONTS.mono,
        fontSize: 30,
        fontWeight: 700,
        color: COLORS.bg,
        background: color,
        borderRadius: 10,
        padding: "6px 18px",
        boxShadow: `0 0 26px -8px ${color}`,
      }}
    >
      FIX {n}
    </div>
    <div
      style={{
        fontFamily: FONTS.display,
        fontWeight: 700,
        fontSize: 58,
        letterSpacing: "-0.02em",
        color: COLORS.text,
      }}
    >
      {title}
    </div>
  </div>
);

const Mono: React.FC<{ children: React.ReactNode; color?: string }> = ({
  children,
  color = COLORS.text,
}) => (
  <div
    style={{
      fontFamily: FONTS.mono,
      fontSize: 21,
      lineHeight: 1.6,
      color,
    }}
  >
    {children}
  </div>
);

/* a reusable before(red) → after(green) pair that reveals in sequence */
const BeforeAfter: React.FC<{
  beforeTitle: string;
  before: React.ReactNode;
  afterTitle: string;
  after: React.ReactNode;
  panelWidth?: number;
}> = ({ beforeTitle, before, afterTitle, after, panelWidth = 540 }) => {
  const frame = useCurrentFrame();
  const reveal = (f: number) =>
    interpolate(frame, [f, f + 18], [0, 1], {
      extrapolateLeft: "clamp",
      extrapolateRight: "clamp",
    });
  return (
    <div style={{ display: "flex", alignItems: "stretch", gap: 30 }}>
      <div style={{ opacity: reveal(10) }}>
        <Panel accent="red" title={beforeTitle} width={panelWidth}>
          {before}
        </Panel>
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          fontFamily: FONTS.display,
          fontWeight: 700,
          fontSize: 52,
          color: COLORS.dim,
          opacity: reveal(46),
        }}
      >
        →
      </div>
      <div style={{ opacity: reveal(60) }}>
        <Panel accent="green" title={afterTitle} width={panelWidth}>
          {after}
        </Panel>
      </div>
    </div>
  );
};

/* 01 — HOOK : the pipeline is green, but four subtle things are still wrong */
export const Scene01Hook: React.FC = () => {
  const frame = useCurrentFrame();
  const four = interpolate(frame, [40, 80], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const green = interpolate(frame, [10, 40], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.hook.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="a debugging war story · the bugs that aren't crashes" color={COLORS.amber} />
          <Big>
            The pipeline is{" "}
            <span style={{ color: COLORS.green, opacity: 0.4 + green * 0.6 }}>
              green
            </span>
            .
            <br />
            <span style={{ color: COLORS.dim }}>Still,</span>{" "}
            <span style={{ color: COLORS.red, opacity: 0.4 + four * 0.6 }}>
              four things
            </span>{" "}
            are wrong.
          </Big>
          <div
            style={{
              opacity: four,
              marginTop: 40,
              fontFamily: FONTS.mono,
              fontSize: 26,
              color: COLORS.text,
              border: `1px solid ${COLORS.red}55`,
              borderRadius: 8,
              padding: "12px 22px",
            }}
          >
            no error · no stack trace ·{" "}
            <span style={{ color: COLORS.red }}>just wrong</span>
          </div>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={HOOK} accent="amber" />
    </AbsoluteFill>
  );
};

/* 02 — FIX 1 : don't cache time */
export const Scene02Fix1: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.fix1.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <FixHeader n={1} title="Don't cache time." color={COLORS.cyan} />
        <BeforeAfter
          beforeTitle="cache · 30-min TTL"
          before={
            <Mono>
              q: <span style={{ color: COLORS.amber }}>"the last 4 days?"</span>
              <br />
              <span style={{ color: COLORS.dim }}>stored answer →</span>
              <br />
              <span style={{ color: COLORS.red }}>served tomorrow = stale</span>
              <br />
              date-relative · still confident
            </Mono>
          }
          afterTitle="temporal-aware cache"
          after={
            <Mono>
              detect:{" "}
              <span style={{ color: COLORS.cyan }}>latest · last N days · recently</span>
              <br />
              <span style={{ color: COLORS.green }}>→ never write to cache</span>
              <br />
              <span style={{ color: COLORS.dim }}>can't serve a stale answer</span>
              <br />
              <span style={{ color: COLORS.dim }}>you never stored</span>
            </Mono>
          }
        />
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={FIX1} accent="cyan" />
  </AbsoluteFill>
);

/* 03 — FIX 2 : count real work, not your own questions */
export const Scene03Fix2: React.FC = () => {
  const frame = useCurrentFrame();
  // the inflated chunk count "deflates" toward the real, agent_output-excluded number
  const chunks = interpolate(frame, [60, 150], [16690, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const drop = interpolate(frame, [60, 120], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.fix2.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <FixHeader n={2} title="Count real work, not your own questions." color={COLORS.amber} />
          <BeforeAfter
            beforeTitle="changelog · what it bragged"
            before={
              <Mono>
                <span style={{ color: COLORS.amber }}>+30 new sources</span>
                <br />
                <span style={{ color: COLORS.amber }}>+16,690 new chunks</span>
                <br />
                <span style={{ color: COLORS.red, opacity: drop }}>
                  ↳ most rows = agent_output
                </span>
                <br />
                <span style={{ color: COLORS.dim, opacity: drop }}>
                  the chat's OWN answers, re-ingested
                </span>
              </Mono>
            }
            afterTitle="exclude agent_output"
            after={
              <Mono>
                <span style={{ color: COLORS.dim }}>real new chunks:</span>{" "}
                <span style={{ color: COLORS.green, fontSize: 26 }}>
                  {Math.round(chunks).toLocaleString()}
                </span>
                <br />
                <span style={{ color: COLORS.green }}>
                  WHERE source ≠ agent_output
                </span>
                <br />
                <span style={{ color: COLORS.dim }}>stop counting questions</span>
                <br />
                <span style={{ color: COLORS.dim }}>as accomplishments</span>
              </Mono>
            }
          />
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={FIX2} accent="amber" />
    </AbsoluteFill>
  );
};

/* 04 — FIX 3 : grade the right thing */
export const Scene04Fix3: React.FC = () => {
  const frame = useCurrentFrame();
  const wasted = interpolate(frame, [70, 130], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.fix3.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <FixHeader n={3} title="Grade the right thing." color={COLORS.magenta} />
          <BeforeAfter
            panelWidth={560}
            beforeTitle="CRAG · graded the wrong artifact"
            before={
              <Mono>
                retrieved chunks →{" "}
                <span style={{ color: COLORS.red }}>INCORRECT</span>
                <br />
                <span style={{ color: COLORS.green }}>but a live date tool answered ✓</span>
                <br />
                <span style={{ color: COLORS.dim }}>gate judged RETRIEVAL,</span>
                <br />
                <span style={{ color: COLORS.dim }}>a TOOL supplied the answer</span>
              </Mono>
            }
            afterTitle="defer to the tool"
            after={
              <Mono>
                temporal query →{" "}
                <span style={{ color: COLORS.green }}>skip CRAG</span>
                <br />
                <span style={{ color: COLORS.green }}>pass through · tool grades</span>
                <br />
                <span style={{ color: COLORS.dim }}>no pointless broader search</span>
                <br />
                <span style={{ color: COLORS.dim }}>no wasted escalation</span>
              </Mono>
            }
          />
          <div
            style={{
              opacity: wasted,
              marginTop: 30,
              fontFamily: FONTS.mono,
              fontSize: 22,
              color: COLORS.red,
              border: `1px solid ${COLORS.red}55`,
              borderRadius: 8,
              padding: "10px 18px",
            }}
          >
            the wrong-target grade wasted ~3–23 s on a broader search that wasn't needed
          </div>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={FIX3} accent="magenta" />
    </AbsoluteFill>
  );
};

/* 05 — FIX 4 : answer the question, then stop */
const PAD_BEFORE = `## Your answer
...the actual answer the user asked for...

## Potential YouTube Content Ideas   ← nobody asked
1. "How we shipped X in a weekend"
2. "The architecture nobody talks about"

## Marketing Roadmap (Q3)            ← nobody asked
- Launch thread · email sequence · ...
# → 3,000+ words`;
const PAD_AFTER = `## Your answer
...the actual answer, in real depth...

# SCOPE DISCIPLINE (prompt rule)
#   depth on the asked question  ✓ keep
#   tangential padding           ✗ drop
# → answer the question, then stop.`;
export const Scene05Fix4: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.fix4.duration} />
    <AbsoluteFill style={{ alignItems: "center", justifyContent: "center", paddingBottom: 30 }}>
      <FixHeader n={4} title="Answer the question, then stop." color={COLORS.violet} />
      <div style={{ display: "flex", gap: 28, alignItems: "flex-start" }}>
        <CodePanel
          code={PAD_BEFORE}
          language="markdown"
          filename="before · answer balloons past 3,000 words"
          highlightLines={[5, 9]}
          revealFrames={150}
          fontSize={18}
          width={760}
        />
        <CodePanel
          code={PAD_AFTER}
          language="markdown"
          filename="after · scope-discipline rule"
          highlightLines={[5, 6, 7]}
          revealFrames={180}
          fontSize={18}
          width={760}
        />
      </div>
    </AbsoluteFill>
    <Caption lines={FIX4} accent="violet" />
  </AbsoluteFill>
);

/* 06 — RECAP : the four fixes as one row · no stack trace, just wrong */
const FIXES: PipeNode[] = [
  { label: "fix 1", value: "don't cache time", sub: "temporal → no cache", accent: "cyan" },
  { label: "fix 2", value: "count real work", sub: "drop agent_output", accent: "amber" },
  { label: "fix 3", value: "grade right", sub: "tool, not retrieval", accent: "magenta" },
  { label: "fix 4", value: "then stop", sub: "scope discipline", accent: "violet" },
];
export const Scene06Recap: React.FC = () => {
  const frame = useCurrentFrame();
  const tag = interpolate(frame, [130, 160], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.recap.duration} />
      <AbsoluteFill style={{ ...center, padding: "0 70px" }}>
        <Kicker text="four fixes · same theme" color={COLORS.green} />
        <div style={{ width: 1700, marginTop: 8 }}>
          <PipelineGraph nodes={FIXES} startFrame={8} stepFrames={22} nodeWidth={340} />
        </div>
        <div
          style={{
            opacity: tag,
            marginTop: 46,
            fontFamily: FONTS.mono,
            fontSize: 24,
            color: COLORS.text,
            border: `1px solid ${COLORS.line}`,
            borderRadius: 8,
            padding: "12px 22px",
          }}
        >
          caching time · counting noise · grading the wrong artifact ·{" "}
          <span style={{ color: COLORS.red }}>over-answering</span>
        </div>
      </AbsoluteFill>
      <Caption lines={RECAP} accent="green" />
    </AbsoluteFill>
  );
};

/* 07 — LESSON : the system runs perfectly and is quietly wrong */
export const Scene07Lesson: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.lesson.duration} />
    <AbsoluteFill style={{ ...center, padding: "0 90px" }}>
      <Kicker text="the lesson · after the crashes" color={COLORS.cyan} />
      <Big size={56}>
        The hardest bugs have{" "}
        <span style={{ color: COLORS.red }}>no stack trace</span> —
        <br />
        where the system runs perfectly
        <br />
        and is <span style={{ color: COLORS.amber }}>quietly wrong.</span>
      </Big>
      <div
        style={{
          marginTop: 44,
          fontFamily: FONTS.mono,
          fontSize: 24,
          color: COLORS.dim,
          textAlign: "center",
        }}
      >
        fixing those is what makes a RAG system feel{" "}
        <span style={{ color: COLORS.green }}>trustworthy.</span>
      </div>
    </AbsoluteFill>
    <Caption lines={LESSON} accent="cyan" />
  </AbsoluteFill>
);

/* 08 — OUTRO */
export const Scene08Outro: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.outro.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Big size={66}>
          The crash is loud.
          <br />
          <span style={{ color: COLORS.cyan }}>The correctness bug just smiles and lies.</span>
        </Big>
        <div
          style={{
            fontFamily: FONTS.mono,
            fontSize: 24,
            letterSpacing: "0.24em",
            color: COLORS.dim,
            marginTop: 44,
          }}
        >
          EAKC · debugging in public
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={OUTRO} accent="cyan" />
  </AbsoluteFill>
);

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
